import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  cleanupExpiredPasswordViewLogs,
  getApprovalNotifierSettings,
  getUserProfile,
  listPasswordViewLogs,
  listUserProfiles,
  logPasswordView,
  setApprovalNotifierSettings,
  setOperatorRole,
  upsertUserProfile,
} from '../lib/firestore';
import {
  decryptPasswordOnClient,
  encryptPasswordOnClient,
  PASSWORD_HINT,
} from '../lib/passwordCipher';
import { mergeFeaturedFarmers } from '../data/farmers';
import { mergeDoctorProfiles } from '../data/doctors';

function formatDateTime(value) {
  if (!value) return '-';
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleString('ja-JP');
  }
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? '-' : dt.toLocaleString('ja-JP');
}

function formatDoctorName(name = '') {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '';
  return trimmed.endsWith('先生') ? trimmed : `${trimmed} 先生`;
}

function toLoginId(email = '', uid = '') {
  const raw = String(email || '').trim();
  if (raw.includes('@')) return raw.split('@')[0];
  return raw || String(uid || '').trim() || '-';
}

function toRoleLabel(role = '') {
  if (role === 'site_admin') return 'サイト管理者';
  if (role === 'doctor') return 'ドクター';
  if (role === 'farmer') return '農家';
  return role || '-';
}

export default function AdminSettingsPage({ mode = 'all' }) {
  const { user, isAdmin, changePassword } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState('');
  const [settingsTab, setSettingsTab] = useState('');
  const isAccountsMode = mode === 'accounts';
  const isSettingsMode = mode === 'settings';

  const [account, setAccount] = useState({
    loginId: '',
    password: '',
    name: '',
    contact: '',
    position: '専業農家',
    role: 'farmer',
    linkedFarmerId: '',
    linkedDoctorId: '',
  });
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountFeedback, setAccountFeedback] = useState({ type: '', message: '' });

  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [notifierAdminOptions, setNotifierAdminOptions] = useState([]);
  const [notifierSelectedUids, setNotifierSelectedUids] = useState([]);
  const [notifierSavedEmails, setNotifierSavedEmails] = useState([]);
  const [notifierBusy, setNotifierBusy] = useState(false);
  const [notifierFeedback, setNotifierFeedback] = useState({ type: '', message: '' });

  const [userOptions, setUserOptions] = useState([]);
  const [checkTargetUid, setCheckTargetUid] = useState('');
  const [checkCipherKey, setCheckCipherKey] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checkBusy, setCheckBusy] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [viewLogs, setViewLogs] = useState([]);
  const [farmerPageOptions, setFarmerPageOptions] = useState([]);
  const [doctorPageOptions, setDoctorPageOptions] = useState([]);
  const [farmerAccountOptions, setFarmerAccountOptions] = useState([]);
  const [doctorAccountOptions, setDoctorAccountOptions] = useState([]);
  const [linkFarmerId, setLinkFarmerId] = useState('');
  const [linkDoctorId, setLinkDoctorId] = useState('');
  const [linkAccountUid, setLinkAccountUid] = useState('');
  const [linkAccountRole, setLinkAccountRole] = useState('farmer');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkFeedback, setLinkFeedback] = useState({ type: '', message: '' });
  const [userList, setUserList] = useState([]);
  const [userEditRoleTab, setUserEditRoleTab] = useState('farmer');
  const [selectedUserUid, setSelectedUserUid] = useState('');
  const [selectedUserProfile, setSelectedUserProfile] = useState({
    name: '',
    contact: '',
    position: '',
    role: '',
    email: '',
    linkedFarmerId: '',
    linkedDoctorId: '',
  });
  const [userEditBusy, setUserEditBusy] = useState(false);
  const [userEditFeedback, setUserEditFeedback] = useState({ type: '', message: '' });

  const gasEndpoint = import.meta.env.VITE_GAS_ACCOUNT_ENDPOINT;
  const gasSharedToken = import.meta.env.VITE_GAS_SHARED_TOKEN || 'drsfarmttadmincontrol';
  const farmerPositionOptions = ['専業農家', 'ライフスタイル農家'];
  const availableTabs = isSettingsMode
    ? ['change', 'notify']
    : ['users', 'create', 'check', 'link', 'change'];

  useEffect(() => {
    const nextTab = searchParams.get('tab');
    if (!nextTab) {
      if (isSettingsMode) {
        setSettingsTab('notify');
      }
      return;
    }
    if (availableTabs.includes(nextTab)) {
      setSettingsTab(nextTab);
    } else if (isSettingsMode) {
      setSettingsTab('notify');
    }
  }, [availableTabs, isSettingsMode, searchParams]);

  function handleAccountRoleChange(nextRole) {
    setAccount((prev) => {
      if (nextRole === 'farmer') {
        const nextPosition = farmerPositionOptions.includes(prev.position) ? prev.position : '専業農家';
        return { ...prev, role: nextRole, position: nextPosition };
      }
      return { ...prev, role: nextRole, position: farmerPositionOptions.includes(prev.position) ? '' : prev.position };
    });
  }

  function handleSelectSettingsTab(tab) {
    setSettingsTab(tab);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setSearchParams(nextParams, { replace: true });
  }

  useEffect(() => {
    async function loadPasswordCheckContext() {
      if (settingsTab !== 'check' || !isAdmin) return;
      const [users, logs] = await Promise.all([
        listUserProfiles(),
        listPasswordViewLogs(120),
      ]);
      setUserOptions(users);
      setViewLogs(logs);
    }
    loadPasswordCheckContext();
  }, [isAdmin, settingsTab]);

  useEffect(() => {
    async function loadLinkContext() {
      if (!['create', 'users', 'link'].includes(settingsTab) || !isAdmin) return;
      const [farmerRowsForAccounts, doctorRowsForAccounts] = await Promise.all([
        listUserProfiles({ role: 'farmer' }),
        listUserProfiles({ role: 'doctor' }),
      ]);
      setFarmerPageOptions(mergeFeaturedFarmers([]));
      setDoctorPageOptions(mergeDoctorProfiles([]));
      setFarmerAccountOptions(farmerRowsForAccounts);
      setDoctorAccountOptions(doctorRowsForAccounts);
    }
    loadLinkContext();
  }, [isAdmin, settingsTab]);

  useEffect(() => {
    async function loadUsersContext() {
      if (settingsTab !== 'users' || !isAdmin) return;
      const rows = await listUserProfiles();
      setUserList(rows);
    }
    loadUsersContext();
  }, [isAdmin, settingsTab]);

  useEffect(() => {
    async function loadNotifierSettings() {
      if (!isSettingsMode || settingsTab !== 'notify' || !isAdmin) return;
      const [row, admins] = await Promise.all([
        getApprovalNotifierSettings(),
        listUserProfiles({ role: 'site_admin' }),
      ]);
      setNotifierAdminOptions(admins);
      setNotifierSavedEmails(row.notifyEmails || []);
      const configuredUids = Array.isArray(row.notifyAdminUids) ? row.notifyAdminUids : [];
      const fallbackUids = (row.notifyEmails || [])
        .map((email) => admins.find((admin) => String(admin.contact || '').trim() === String(email || '').trim())?.uid || '')
        .filter(Boolean);
      const selected = configuredUids.length ? configuredUids : fallbackUids;
      setNotifierSelectedUids(selected);
    }
    loadNotifierSettings();
  }, [isAdmin, isSettingsMode, settingsTab]);

  useEffect(() => {
    async function loadSelectedUser() {
      if (settingsTab !== 'users' || !isAdmin || !selectedUserUid) return;
      const row = userList.find((item) => item.uid === selectedUserUid);
      const profile = row || await getUserProfile(selectedUserUid);
      if (!profile) return;
      setSelectedUserProfile({
        name: profile.name || '',
        contact: profile.contact || '',
        position: profile.position || '',
        role: profile.role || '',
        email: profile.email || '',
        linkedFarmerId: profile.linkedFarmerId || '',
        linkedDoctorId: profile.linkedDoctorId || '',
      });
    }
    loadSelectedUser();
  }, [isAdmin, selectedUserUid, settingsTab, userList]);

  const editableUsers = useMemo(
    () => userList.filter((row) => row.role === userEditRoleTab),
    [userEditRoleTab, userList]
  );

  async function syncFarmerAccountLink(accountUid, farmerId) {
    if (!farmerId) {
      await upsertUserProfile(accountUid, {
        linkedFarmerId: '',
        linkedFarmerName: '',
        linkedFarmerSlug: '',
      });
      return;
    }

    const targetFarmer = farmerPageOptions.find((row) => row.id === farmerId);
    if (!targetFarmer) {
      throw new Error('link-farmer-not-found');
    }

    const farmerUsers = await listUserProfiles({ role: 'farmer' });
    await Promise.all(
      farmerUsers
        .filter((row) => row.uid !== accountUid && row.linkedFarmerId === farmerId)
        .map((row) => upsertUserProfile(row.uid, {
          linkedFarmerId: '',
          linkedFarmerName: '',
          linkedFarmerSlug: '',
        }))
    );

    await upsertUserProfile(accountUid, {
      role: 'farmer',
      linkedFarmerId: farmerId,
      linkedFarmerName: targetFarmer.name || '',
      linkedFarmerSlug: targetFarmer.slug || '',
      linkedAt: new Date().toISOString(),
      linkedBy: user.uid,
    });
  }

  async function syncDoctorAccountLink(accountUid, doctorId) {
    if (!doctorId) {
      await upsertUserProfile(accountUid, {
        linkedDoctorId: '',
        linkedDoctorName: '',
        linkedDoctorPath: '',
      });
      return;
    }

    const targetDoctor = doctorPageOptions.find((row) => row.id === doctorId);
    if (!targetDoctor) {
      throw new Error('link-doctor-not-found');
    }

    const doctorUsers = await listUserProfiles({ role: 'doctor' });
    await Promise.all(
      doctorUsers
        .filter((row) => row.uid !== accountUid && row.linkedDoctorId === doctorId)
        .map((row) => upsertUserProfile(row.uid, {
          linkedDoctorId: '',
          linkedDoctorName: '',
          linkedDoctorPath: '',
        }))
    );

    await upsertUserProfile(accountUid, {
      role: 'doctor',
      linkedDoctorId: doctorId,
      linkedDoctorName: targetDoctor.name || '',
      linkedDoctorPath: targetDoctor.detailPagePath || '',
      linkedAt: new Date().toISOString(),
      linkedBy: user.uid,
    });
  }

  async function handleCreateAccount(event) {
    event.preventDefault();
    setAccountFeedback({ type: '', message: '' });
    if (!isAdmin) {
      setAccountFeedback({ type: 'error', message: 'この操作はサイト管理者のみ実行できます。' });
      return;
    }
    if (!gasEndpoint) {
      setAccountFeedback({ type: 'error', message: 'GASエンドポイントが未設定です。' });
      return;
    }

    const normalizedId = account.loginId.trim().toLowerCase().replace(/@.*/, '');
    if (!normalizedId) {
      setAccountFeedback({ type: 'error', message: 'IDを入力してください。' });
      return;
    }

    setAccountBusy(true);
    try {
      const email = `${normalizedId}@drsfarm.jp`;
      const payload = {
        action: 'createUser',
        email,
        password: account.password,
        name: account.name,
        contact: account.contact,
        position: account.position,
        role: account.role,
        requestedBy: user.uid,
        token: gasSharedToken,
      };

      const response = await fetch(gasEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`gas-error-${response.status}`);

      const data = await response.json();
      if (!data?.uid) throw new Error('missing-uid');

      if (account.role === 'site_admin') {
        await setOperatorRole(data.uid, 'site_admin', user.uid);
      }

      const encryptedPassword = encryptPasswordOnClient(account.password);
      await upsertUserProfile(data.uid, {
        name: account.name,
        contact: account.contact,
        position: account.position,
        role: account.role,
        email,
        linkedFarmerId: '',
        linkedDoctorId: '',
        ...encryptedPassword,
        passwordEncryptedAt: new Date().toISOString(),
        createdBy: user.uid,
      });

      if (account.role === 'farmer') {
        await syncFarmerAccountLink(data.uid, account.linkedFarmerId);
      }
      if (account.role === 'doctor') {
        await syncDoctorAccountLink(data.uid, account.linkedDoctorId);
      }

      setStatus(`アカウントを作成しました（ID: ${normalizedId}）。`);
      setAccountFeedback({ type: 'success', message: `作成完了: ID:${normalizedId}` });
      setAccount({ loginId: '', password: '', name: '', contact: '', position: '専業農家', role: 'farmer', linkedFarmerId: '', linkedDoctorId: '' });
    } catch (err) {
      const message = typeof err?.message === 'string' ? err.message : 'unknown-error';
      setAccountFeedback({ type: 'error', message: `作成失敗: ${message}` });
    } finally {
      setAccountBusy(false);
    }
  }

  async function handleChangePassword(event) {
    event.preventDefault();
    setPasswordMessage('');
    setPasswordError('');

    if (nextPassword.length < 8) {
      setPasswordError('新しいパスワードは8文字以上で入力してください。');
      return;
    }
    if (nextPassword !== confirmPassword) {
      setPasswordError('新しいパスワードと確認用パスワードが一致しません。');
      return;
    }

    setPasswordBusy(true);
    try {
      await changePassword(currentPassword, nextPassword);
      const encryptedPassword = encryptPasswordOnClient(nextPassword);
      await upsertUserProfile(user.uid, {
        ...encryptedPassword,
        passwordEncryptedAt: new Date().toISOString(),
        passwordUpdatedAt: new Date().toISOString(),
        passwordUpdatedBy: user.uid,
      });
      setPasswordMessage('パスワードを変更しました。');
      setCurrentPassword('');
      setNextPassword('');
      setConfirmPassword('');
    } catch {
      setPasswordError('パスワード変更に失敗しました。現在のパスワードを確認してください。');
    } finally {
      setPasswordBusy(false);
    }
  }

  async function handleCheckPasswordStore(event) {
    event.preventDefault();
    setCheckResult(null);
    setCheckError('');
    if (!isAdmin) {
      setCheckError('この操作はサイト管理者のみ実行できます。');
      return;
    }
    if (!checkTargetUid) {
      setCheckError('確認対象アカウントを選択してください。');
      return;
    }

    setCheckBusy(true);
    try {
      const row = await getUserProfile(checkTargetUid);
      if (!row) {
        setCheckError('対象ユーザーのプロフィールが見つかりません。');
        return;
      }

      const decryptedPassword = decryptPasswordOnClient(row, checkCipherKey);
      setCheckResult({
        uid: checkTargetUid,
        name: row.name || '-',
        email: row.email || '-',
        role: row.role || '-',
        encryptedAt: row.passwordEncryptedAt || '-',
        passwordPlainText: decryptedPassword,
      });

      await logPasswordView({
        actorUid: user.uid,
        actorEmail: user.email || '',
        targetUid: checkTargetUid,
        targetEmail: row.email || '',
        targetRole: row.role || '',
        scope: 'admin_settings',
      });
      await cleanupExpiredPasswordViewLogs();
      setViewLogs(await listPasswordViewLogs(120));
    } catch (err) {
      setCheckError(err?.message === 'invalid-cipher-key' ? '復号キーが正しくありません。' : 'パスワード復号に失敗しました。');
    } finally {
      setCheckBusy(false);
    }
  }

  async function handleLinkFarmerPage(event) {
    event.preventDefault();
    setLinkFeedback({ type: '', message: '' });
    if (!isAdmin) {
      setLinkFeedback({ type: 'error', message: 'この操作はサイト管理者のみ実行できます。' });
      return;
    }
    if (!linkAccountUid) {
      setLinkFeedback({ type: 'error', message: '紐付け対象アカウントを選択してください。' });
      return;
    }
    if (linkAccountRole === 'farmer' && !linkFarmerId) {
      setLinkFeedback({ type: 'error', message: '農家ページを選択してください。' });
      return;
    }
    if (linkAccountRole === 'doctor' && !linkDoctorId) {
      setLinkFeedback({ type: 'error', message: 'ドクターページを選択してください。' });
      return;
    }

    setLinkBusy(true);
    try {
      if (linkAccountRole === 'farmer') {
        const targetFarmer = farmerPageOptions.find((row) => row.id === linkFarmerId);
        const targetAccount = farmerAccountOptions.find((row) => row.uid === linkAccountUid);
        if (!targetFarmer || !targetAccount) {
          throw new Error('link-target-not-found');
        }

        await syncFarmerAccountLink(linkAccountUid, linkFarmerId);
        setLinkFeedback({
          type: 'success',
          message: `${targetFarmer.name} と ${targetAccount.name || targetAccount.email || targetAccount.uid} を紐付けました。`,
        });
      } else {
        const targetDoctor = doctorPageOptions.find((row) => row.id === linkDoctorId);
        const targetAccount = doctorAccountOptions.find((row) => row.uid === linkAccountUid);
        if (!targetDoctor || !targetAccount) {
          throw new Error('link-target-not-found');
        }

        await syncDoctorAccountLink(linkAccountUid, linkDoctorId);
        setLinkFeedback({
          type: 'success',
          message: `${formatDoctorName(targetDoctor.name)} と ${targetAccount.name || targetAccount.email || targetAccount.uid} を紐付けました。`,
        });
      }
      const [farmerRowsForAccounts, doctorRowsForAccounts, rows] = await Promise.all([
        listUserProfiles({ role: 'farmer' }),
        listUserProfiles({ role: 'doctor' }),
        listUserProfiles(),
      ]);
      setFarmerPageOptions(mergeFeaturedFarmers([]));
      setDoctorPageOptions(mergeDoctorProfiles([]));
      setFarmerAccountOptions(farmerRowsForAccounts);
      setDoctorAccountOptions(doctorRowsForAccounts);
      setUserList(rows);
    } catch (err) {
      setLinkFeedback({ type: 'error', message: err?.message === 'link-target-not-found' ? '紐付け対象が見つかりません。' : '紐付けに失敗しました。' });
    } finally {
      setLinkBusy(false);
    }
  }

  async function handleUpdateUserProfile(event) {
    event.preventDefault();
    setUserEditFeedback({ type: '', message: '' });
    if (!isAdmin) {
      setUserEditFeedback({ type: 'error', message: 'この操作はサイト管理者のみ実行できます。' });
      return;
    }
    if (!selectedUserUid) {
      setUserEditFeedback({ type: 'error', message: '編集対象ユーザーを選択してください。' });
      return;
    }

    setUserEditBusy(true);
    try {
      await upsertUserProfile(selectedUserUid, {
        name: selectedUserProfile.name,
        contact: selectedUserProfile.contact,
        position: selectedUserProfile.position,
      });
      if (selectedUserProfile.role === 'farmer') {
        await syncFarmerAccountLink(selectedUserUid, selectedUserProfile.linkedFarmerId);
      }
      if (selectedUserProfile.role === 'doctor') {
        await syncDoctorAccountLink(selectedUserUid, selectedUserProfile.linkedDoctorId);
      }
      const rows = await listUserProfiles();
      setUserList(rows);
      setUserEditFeedback({ type: 'success', message: 'ユーザー情報を更新しました。' });
    } catch {
      setUserEditFeedback({ type: 'error', message: 'ユーザー情報の更新に失敗しました。' });
    } finally {
      setUserEditBusy(false);
    }
  }

  async function handleSaveNotifierSettings(event) {
    event.preventDefault();
    setNotifierFeedback({ type: '', message: '' });
    if (!isAdmin) {
      setNotifierFeedback({ type: 'error', message: 'この操作はサイト管理者のみ実行できます。' });
      return;
    }
    const selectedRows = notifierAdminOptions.filter((row) => notifierSelectedUids.includes(row.uid));
    const notifyEmails = selectedRows
      .flatMap((row) => String(row.contact || '')
        .split(/[,\s;]+/)
        .map((item) => item.trim())
        .filter(Boolean))
      .filter((value) => value.includes('@'));
    if (!notifyEmails.length) {
      setNotifierFeedback({ type: 'error', message: '連絡先にメールアドレスが設定されたサイト管理者を1名以上選択してください。' });
      return;
    }

    setNotifierBusy(true);
    try {
      await setApprovalNotifierSettings({
        enabled: true,
        notifyAdminUids: notifierSelectedUids,
        notifyEmails,
      }, user.uid);
      setNotifierSavedEmails(notifyEmails);
      setNotifierFeedback({ type: 'success', message: '承認待ち通知メールの宛先を保存しました。' });
    } catch (error) {
      setNotifierFeedback({ type: 'error', message: `保存に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
    } finally {
      setNotifierBusy(false);
    }
  }

  function toggleNotifierAdmin(uid) {
    setNotifierSelectedUids((prev) => (
      prev.includes(uid)
        ? prev.filter((rowUid) => rowUid !== uid)
        : [...prev, uid]
    ));
  }

  return (
    <>
      <Helmet><title>{`Drs.Farm | ${isAccountsMode ? 'アカウント管理' : '設定'}`}</title></Helmet>
      <h1>{isAccountsMode ? 'アカウント管理' : '設定'}</h1>
      <p className="hint">
        {isAccountsMode
          ? '新規アカウント作成、ユーザー情報修正、パスワード確認、ページ紐付けを行います。'
          : '管理者自身のパスワード変更など、共通設定を行います。'}
      </p>
      {status ? <p className="success-text">{status}</p> : null}

      <section className="panel">
        <div className="row-actions">
          <Link className="btn btn-secondary" to="/admin">管理者メニューへ戻る</Link>
        </div>
      </section>

      <section className="admin-grid">
        <article className="panel form-grid">
          {isAccountsMode ? (
            <>
              <div className="row-actions">
                <button
                  type="button"
                  className={`btn ${settingsTab === 'users' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleSelectSettingsTab('users')}
                >
                  ユーザー一覧
                </button>
                <button
                  type="button"
                  className={`btn ${settingsTab === 'create' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleSelectSettingsTab('create')}
                >
                  新規アカウント作成
                </button>
                <button
                  type="button"
                  className={`btn ${settingsTab === 'check' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleSelectSettingsTab('check')}
                >
                  パスワード確認
                </button>
                <button
                  type="button"
                  className={`btn ${settingsTab === 'link' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleSelectSettingsTab('link')}
                >
                  ページとの紐付け
                </button>
              </div>

              {!settingsTab ? <p className="hint">上のメニューから項目を選択すると入力欄が表示されます。</p> : null}
            </>
          ) : null}

          {isSettingsMode ? (
            <div className="row-actions">
              <button
                type="button"
                className={`btn ${settingsTab === 'notify' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => handleSelectSettingsTab('notify')}
              >
                承認通知メール
              </button>
              <button
                type="button"
                className={`btn ${settingsTab === 'change' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => handleSelectSettingsTab('change')}
              >
                パスワード変更
              </button>
            </div>
          ) : null}

          {!isSettingsMode && settingsTab === 'users' ? (
            <>
              <h2>ユーザー一覧と情報修正</h2>
              {isAdmin ? (
                <>
                  <div className="tab-row" role="tablist" aria-label="ユーザー種別タブ">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={userEditRoleTab === 'farmer'}
                      className={`tab-btn ${userEditRoleTab === 'farmer' ? 'is-active' : ''}`}
                      onClick={() => {
                        setUserEditRoleTab('farmer');
                        setSelectedUserUid('');
                        setUserEditFeedback({ type: '', message: '' });
                      }}
                    >
                      農家
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={userEditRoleTab === 'doctor'}
                      className={`tab-btn ${userEditRoleTab === 'doctor' ? 'is-active' : ''}`}
                      onClick={() => {
                        setUserEditRoleTab('doctor');
                        setSelectedUserUid('');
                        setUserEditFeedback({ type: '', message: '' });
                      }}
                    >
                      ドクター
                    </button>
                  </div>

                  <section className="admin-grid user-edit-grid">
                    <article className="panel">
                      <div className="directory-head">
                        <div>
                          <h3>{userEditRoleTab === 'farmer' ? '農家アカウント一覧' : 'ドクターアカウント一覧'}</h3>
                          <p className="hint">カードを選ぶと右側で編集できます。</p>
                        </div>
                        <p className="directory-count">{editableUsers.length}件</p>
                      </div>
                      {editableUsers.length === 0 ? (
                        <p className="hint">対象ユーザーがいません。</p>
                      ) : (
                        <ul className="directory-list">
                          {editableUsers.map((row) => (
                            <li key={row.uid}>
                              <button
                                type="button"
                                className={`directory-row user-select-card ${selectedUserUid === row.uid ? 'is-active' : ''}`}
                                onClick={() => {
                                  setSelectedUserUid(row.uid);
                                  setUserEditFeedback({ type: '', message: '' });
                                }}
                              >
                                <p><strong>{row.name || '名称未設定'}</strong></p>
                                <p className="directory-meta">ID: {toLoginId(row.email, row.uid)}</p>
                                <p className="directory-meta">権限: {toRoleLabel(row.role)}</p>
                                <p className="directory-meta">{row.contact || '連絡先未設定'} / {row.position || '役職未設定'}</p>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </article>

                    <article className="panel">
                      <h3>ユーザー情報の編集</h3>
                      {selectedUserUid ? (
                        <form className="form-grid" onSubmit={handleUpdateUserProfile}>
                          <div>
                            <p className="hint">ID</p>
                            <p><strong>{toLoginId(selectedUserProfile.email, selectedUserUid)}</strong></p>
                          </div>
                          <label>権限
                            <input value={toRoleLabel(selectedUserProfile.role)} readOnly />
                          </label>
                          <label>氏名
                            <input
                              value={selectedUserProfile.name}
                              onChange={(e) => setSelectedUserProfile((prev) => ({ ...prev, name: e.target.value }))}
                            />
                          </label>
                          <label>連絡先
                            <input
                              value={selectedUserProfile.contact}
                              onChange={(e) => setSelectedUserProfile((prev) => ({ ...prev, contact: e.target.value }))}
                            />
                          </label>
                          <label>役職
                            <input
                              value={selectedUserProfile.position}
                              onChange={(e) => setSelectedUserProfile((prev) => ({ ...prev, position: e.target.value }))}
                            />
                          </label>
                          {selectedUserProfile.role === 'farmer' ? (
                            <label>紐付ける農家ページ
                              <select
                                value={selectedUserProfile.linkedFarmerId}
                                onChange={(e) => setSelectedUserProfile((prev) => ({ ...prev, linkedFarmerId: e.target.value }))}
                              >
                                <option value="">選択しない / 解除する</option>
                                {farmerPageOptions.map((row) => (
                                  <option key={row.id} value={row.id}>
                                    {row.name}
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : null}
                          {selectedUserProfile.role === 'doctor' ? (
                            <label>紐付けるドクターページ
                              <select
                                value={selectedUserProfile.linkedDoctorId}
                                onChange={(e) => setSelectedUserProfile((prev) => ({ ...prev, linkedDoctorId: e.target.value }))}
                              >
                                <option value="">選択しない / 解除する</option>
                                {doctorPageOptions.map((row) => (
                                  <option key={row.id} value={row.id}>
                                    {formatDoctorName(row.name || row.id)}
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : null}
                          <button type="submit" className="btn btn-primary" disabled={userEditBusy}>
                            {userEditBusy ? '更新中...' : 'ユーザー情報を更新'}
                          </button>
                          {userEditFeedback.message ? (
                            <p className={userEditFeedback.type === 'success' ? 'success-text' : 'error-text'}>
                              {userEditFeedback.message}
                            </p>
                          ) : null}
                        </form>
                      ) : (
                        <p className="hint">左のカードを選択してください。</p>
                      )}
                    </article>
                  </section>
                </>
              ) : (
                <p>この項目はサイト管理者のみ利用できます。</p>
              )}
            </>
          ) : null}

          {!isSettingsMode && settingsTab === 'create' ? (
            <>
              <h2>新規アカウント作成</h2>
              {isAdmin ? (
                <form onSubmit={handleCreateAccount} className="form-grid">
                  <label>ID<input value={account.loginId} onChange={(e) => setAccount({ ...account, loginId: e.target.value })} placeholder="例: tanaka01" required /></label>
                  <p className="hint">@drsfarm.jp は自動で付与されます。</p>
                  <label>初期パスワード<input type="password" value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} required /></label>
                  <label>氏名<input value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })} required /></label>
                  <label>連絡先<input value={account.contact} onChange={(e) => setAccount({ ...account, contact: e.target.value })} required /></label>
                  <label>権限
                    <select value={account.role} onChange={(e) => handleAccountRoleChange(e.target.value)}>
                      <option value="site_admin">サイト管理者</option>
                      <option value="doctor">ドクター</option>
                      <option value="farmer">農家</option>
                    </select>
                  </label>
                  {account.role === 'farmer' ? (
                    <label>農家区分
                      <select value={account.position} onChange={(e) => setAccount({ ...account, position: e.target.value })} required>
                        {farmerPositionOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <label>役職<input value={account.position} onChange={(e) => setAccount({ ...account, position: e.target.value })} required /></label>
                  )}
                  {account.role === 'farmer' ? (
                    <label>紐付ける農家ページ
                      <select value={account.linkedFarmerId} onChange={(e) => setAccount({ ...account, linkedFarmerId: e.target.value })}>
                        <option value="">後で設定する</option>
                        {farmerPageOptions.map((row) => (
                          <option key={row.id} value={row.id}>
                            {row.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  {account.role === 'doctor' ? (
                    <label>紐付けるドクターページ
                      <select value={account.linkedDoctorId} onChange={(e) => setAccount({ ...account, linkedDoctorId: e.target.value })}>
                        <option value="">後で設定する</option>
                        {doctorPageOptions.map((row) => (
                          <option key={row.id} value={row.id}>
                            {formatDoctorName(row.name || row.id)}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  <button className="btn btn-primary" type="submit" disabled={accountBusy}>{accountBusy ? '作成中...' : 'アカウント作成'}</button>
                  {accountFeedback.message ? <p className={accountFeedback.type === 'success' ? 'success-text' : 'error-text'}>{accountFeedback.message}</p> : null}
                </form>
              ) : (
                <p>この操作はサイト管理者のみ実行できます。</p>
              )}
            </>
          ) : null}

          {settingsTab === 'notify' ? (
            <>
              <h2>承認待ち通知メール</h2>
              {isAdmin ? (
                <form className="form-grid" onSubmit={handleSaveNotifierSettings}>
                  <p className="hint">通知先はサイト管理者アカウントから選択してください（直接入力はできません）。</p>
                  <div className="info-callout info-callout-compact">
                    <p className="info-callout-title">現在保存されている通知先</p>
                    {notifierSavedEmails.length ? (
                      <ul className="plain-list notifier-saved-list">
                        {notifierSavedEmails.map((email) => (
                          <li key={email}>{email}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="hint">保存済みの通知先はありません。</p>
                    )}
                  </div>
                  {notifierAdminOptions.length === 0 ? (
                    <p className="hint">サイト管理者アカウントがありません。</p>
                  ) : (
                    <ul className="directory-list">
                      {notifierAdminOptions.map((row) => (
                        <li key={row.uid}>
                          <label className="directory-row notifier-admin-row">
                            <input
                              type="checkbox"
                              checked={notifierSelectedUids.includes(row.uid)}
                              onChange={() => toggleNotifierAdmin(row.uid)}
                            />
                            <div>
                              <p><strong>{row.name || toLoginId(row.email, row.uid)}</strong></p>
                              <p className="directory-meta">連絡先: {String(row.contact || '未設定')}</p>
                            </div>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button type="submit" className="btn btn-primary" disabled={notifierBusy}>
                    {notifierBusy ? '保存中...' : '通知先を保存'}
                  </button>
                  {notifierFeedback.message ? (
                    <p className={notifierFeedback.type === 'success' ? 'success-text' : 'error-text'}>{notifierFeedback.message}</p>
                  ) : null}
                </form>
              ) : (
                <p>この項目はサイト管理者のみ利用できます。</p>
              )}
            </>
          ) : null}

          {settingsTab === 'change' ? (
            <>
              <h2>パスワード変更</h2>
              <p className="hint">ログイン中アカウント: {user?.email || '-'}</p>
              <form className="form-grid" onSubmit={handleChangePassword}>
                <input type="text" autoComplete="username" value={user?.email || ''} readOnly hidden aria-hidden="true" tabIndex={-1} />
                <label>現在のパスワード<input type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required /></label>
                <label>新しいパスワード<input type="password" autoComplete="new-password" value={nextPassword} onChange={(e) => setNextPassword(e.target.value)} required /></label>
                <label>新しいパスワード（確認）<input type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required /></label>
                {passwordError ? <p className="error-text">{passwordError}</p> : null}
                {passwordMessage ? <p className="success-text">{passwordMessage}</p> : null}
                <button type="submit" className="btn btn-primary" disabled={passwordBusy}>{passwordBusy ? '更新中...' : 'パスワードを変更'}</button>
              </form>
            </>
          ) : null}

          {!isSettingsMode && settingsTab === 'check' ? (
            <>
              <h2>パスワード確認</h2>
              {isAdmin ? (
                <>
                  <p className="hint">確認対象は全アカウントから選択できます。復号キーを入力しないと表示されません。</p>
                  <form className="form-grid" onSubmit={handleCheckPasswordStore}>
                    <input type="text" autoComplete="username" value={checkResult?.email || user?.email || ''} readOnly hidden aria-hidden="true" tabIndex={-1} />
                    <label>確認対象アカウント
                      <select value={checkTargetUid} onChange={(e) => setCheckTargetUid(e.target.value)} required>
                        <option value="">選択してください</option>
                        {userOptions.map((row) => (
                          <option key={row.uid} value={row.uid}>{row.email || row.uid} / {row.name || '-'} / {row.role || '-'}</option>
                        ))}
                      </select>
                    </label>
                    <label>復号キー
                      <input type="password" autoComplete="new-password" value={checkCipherKey} onChange={(e) => setCheckCipherKey(e.target.value)} placeholder={PASSWORD_HINT} required />
                    </label>
                    <button type="submit" className="btn btn-secondary" disabled={checkBusy}>{checkBusy ? '確認中...' : '復号して確認'}</button>
                  </form>
                  {checkError ? <p className="error-text">{checkError}</p> : null}
                  {checkResult ? (
                    <div className="hint">
                      <p>UID: {checkResult.uid}</p>
                      <p>氏名: {checkResult.name}</p>
                      <p>メール: {checkResult.email}</p>
                      <p>権限: {checkResult.role}</p>
                      <p>暗号化日時: {checkResult.encryptedAt}</p>
                      <p><strong>パスワード: {checkResult.passwordPlainText}</strong></p>
                    </div>
                  ) : null}
                  <h3>パスワード閲覧ログ（半年分）</h3>
                  {viewLogs.length === 0 ? <p className="hint">ログはまだありません。</p> : (
                    <ul className="plain-list">
                      {viewLogs.map((log) => (
                        <li key={log.id} className="list-row">
                          <div>
                            <p>{formatDateTime(log.viewedAt)} / 閲覧者: {log.actorEmail || log.actorUid}</p>
                            <p className="hint">対象: {log.targetEmail || log.targetUid} ({log.targetRole || '-'}) / scope: {log.scope || '-'}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <p>この項目はサイト管理者のみ利用できます。</p>
              )}
            </>
          ) : null}

          {!isSettingsMode && settingsTab === 'link' ? (
            <>
              <h2>アカウントとページの紐付け</h2>
              {isAdmin ? (
                <>
                  <p className="hint">農家またはドクターのアカウントと、公開サイト上の紹介ページを紐付けます。すでに同じページへ紐付いている別アカウントがある場合は自動で上書きします。</p>
                  <div className="tab-row" role="tablist" aria-label="紐付け対象タブ">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={linkAccountRole === 'farmer'}
                      className={`tab-btn ${linkAccountRole === 'farmer' ? 'is-active' : ''}`}
                      onClick={() => {
                        setLinkAccountRole('farmer');
                        setLinkAccountUid('');
                        setLinkFarmerId('');
                        setLinkFeedback({ type: '', message: '' });
                      }}
                    >
                      農家を紐付け
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={linkAccountRole === 'doctor'}
                      className={`tab-btn ${linkAccountRole === 'doctor' ? 'is-active' : ''}`}
                      onClick={() => {
                        setLinkAccountRole('doctor');
                        setLinkAccountUid('');
                        setLinkDoctorId('');
                        setLinkFeedback({ type: '', message: '' });
                      }}
                    >
                      ドクターを紐付け
                    </button>
                  </div>

                  <form className="form-grid" onSubmit={handleLinkFarmerPage}>
                    <label>{linkAccountRole === 'farmer' ? '紐付ける農家ページ' : '紐付けるドクターページ'}
                      {linkAccountRole === 'farmer' ? (
                        <select value={linkFarmerId} onChange={(e) => setLinkFarmerId(e.target.value)} required>
                          <option value="">選択してください</option>
                          {farmerPageOptions.map((row) => (
                            <option key={row.id} value={row.id}>
                              {row.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select value={linkDoctorId} onChange={(e) => setLinkDoctorId(e.target.value)} required>
                          <option value="">選択してください</option>
                          {doctorPageOptions.map((row) => (
                            <option key={row.id} value={row.id}>
                              {formatDoctorName(row.name || row.id)}
                            </option>
                          ))}
                        </select>
                      )}
                    </label>
                    <label>{linkAccountRole === 'farmer' ? '紐付ける農家アカウント' : '紐付けるドクターアカウント'}
                      <select value={linkAccountUid} onChange={(e) => setLinkAccountUid(e.target.value)} required>
                        <option value="">選択してください</option>
                        {(linkAccountRole === 'farmer' ? farmerAccountOptions : doctorAccountOptions).map((row) => (
                          <option key={row.uid} value={row.uid}>
                            {(row.name || row.email || row.uid)} / {row.email || row.uid}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button type="submit" className="btn btn-primary" disabled={linkBusy}>
                      {linkBusy ? '紐付け中...' : '紐付けを保存'}
                    </button>
                    {linkFeedback.message ? (
                      <p className={linkFeedback.type === 'success' ? 'success-text' : 'error-text'}>
                        {linkFeedback.message}
                      </p>
                    ) : null}
                  </form>

                  <section className="admin-grid link-status-grid">
                    <article className="panel">
                      <div className="directory-head">
                        <div>
                          <h3>農家ページの紐付け状況</h3>
                          <p className="hint">ページごとのアカウント紐付け状態です。</p>
                        </div>
                        <p className="directory-count">{farmerPageOptions.length}件</p>
                      </div>
                      <ul className="directory-list">
                        {farmerPageOptions.map((row) => {
                          const linkedAccount = farmerAccountOptions.find((accountRow) => accountRow.linkedFarmerId === row.id);
                          const linkedLabel = linkedAccount
                            ? `${linkedAccount.name || linkedAccount.email || linkedAccount.uid}`
                            : '未設定';
                          return (
                            <li key={row.id} className="directory-row link-status-row">
                              <div className="link-status-head">
                                <p><strong>{row.name}</strong></p>
                                <span className={`link-status-badge ${linkedAccount ? 'is-linked' : 'is-unlinked'}`}>
                                  {linkedAccount ? '紐付け済み' : '未紐付け'}
                                </span>
                              </div>
                              <p className="directory-meta">紐付け先: {linkedLabel}</p>
                            </li>
                          );
                        })}
                      </ul>
                    </article>

                    <article className="panel">
                      <div className="directory-head">
                        <div>
                          <h3>ドクターページの紐付け状況</h3>
                          <p className="hint">ページごとのアカウント紐付け状態です。</p>
                        </div>
                        <p className="directory-count">{doctorPageOptions.length}件</p>
                      </div>
                      <ul className="directory-list">
                        {doctorPageOptions.map((row) => {
                          const linkedAccount = doctorAccountOptions.find((accountRow) => accountRow.linkedDoctorId === row.id);
                          const linkedLabel = linkedAccount
                            ? `${linkedAccount.name || linkedAccount.email || linkedAccount.uid}`
                            : '未設定';
                          return (
                            <li key={row.id} className="directory-row link-status-row">
                              <div className="link-status-head">
                                <p><strong>{formatDoctorName(row.name || row.id)}</strong></p>
                                <span className={`link-status-badge ${linkedAccount ? 'is-linked' : 'is-unlinked'}`}>
                                  {linkedAccount ? '紐付け済み' : '未紐付け'}
                                </span>
                              </div>
                              <p className="directory-meta">紐付け先: {linkedLabel}</p>
                            </li>
                          );
                        })}
                      </ul>
                    </article>
                  </section>
                </>
              ) : (
                <p>この項目はサイト管理者のみ利用できます。</p>
              )}
            </>
          ) : null}

        </article>
      </section>
    </>
  );
}
